<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen">
    <div class="container mx-auto">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold text-gray-100">Coupons</h1>
        <div class="flex items-center gap-3">
          <!-- Contextual Action Buttons -->
          <div v-if="selectedCouponIds.size > 0" class="flex items-center gap-3">
            <span class="text-sm text-gray-300">
              <span class="font-semibold">{{ selectedCouponIds.size }}</span>
              <span class="ml-1">selected</span>
            </span>
            <div class="flex gap-2">
              <!-- Active Status: Assign, Deactivate -->
              <template v-if="selectedStatus === 'active'">
                <button
                  @click="openAssignModal"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Assign
                </button>
                <button
                  @click="handleBulkDeactivate"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Deactivate
                </button>
              </template>

              <!-- Assigned Status: Unassign, Deactivate -->
              <template v-if="selectedStatus === 'assigned'">
                <button
                  @click="handleBulkUnassign"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Unassign
                </button>
                <button
                  @click="handleBulkDeactivate"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Deactivate
                </button>
              </template>

              <!-- Used Status: No actions -->
              <template v-if="selectedStatus === 'used'">
                <span class="text-sm text-gray-500 italic px-2">No actions available</span>
              </template>

              <!-- Deactivated Status: Activate, Delete -->
              <template v-if="selectedStatus === 'deactivated'">
                <button
                  @click="handleBulkActivate"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Activate
                </button>
                <button
                  @click="handleBulkDelete"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete
                </button>
              </template>

              <!-- Expired Status: Delete -->
              <template v-if="selectedStatus === 'expired'">
                <button
                  @click="handleBulkDelete"
                  :disabled="isPerformingAction"
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete
                </button>
              </template>
            </div>
          </div>
          <button
            @click="openGenerateModal"
            :disabled="loading"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Generate Coupon
          </button>
        </div>
      </div>
      
      <!-- Filters and Total -->
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <label class="text-sm font-medium text-gray-300 whitespace-nowrap">Status:</label>
            <select
              v-model="selectedStatus"
              @change="handleStatusChange"
              :disabled="loading"
              class="px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed min-w-[150px]"
            >
              <option value="active">Active</option>
              <option value="assigned">Assigned</option>
              <option value="used">Used</option>
              <option value="deactivated">Deactivated</option>
              <option value="expired">Expired</option>
              <option value="">All</option>
            </select>
            
            <!-- Month Navigation (only for used status) -->
            <template v-if="selectedStatus === 'used'">
              <div class="flex items-center gap-4 ml-4">
                <button
                  @click="previousMonth"
                  :disabled="loading"
                  class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
                >
                  &larr;
                </button>
                <div class="text-lg font-semibold text-gray-100 min-w-[120px] text-center">
                  {{ currentMonthDisplay }}
                </div>
                <button
                  @click="nextMonth"
                  :disabled="loading"
                  class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
                >
                  &rarr;
                </button>
              </div>
            </template>
            
            <template v-if="selectedStatus !== 'active'">
              <label class="text-sm font-medium text-gray-300 whitespace-nowrap ml-4">Endorser:</label>
              <select
                v-model="selectedEndorserCode"
                @change="fetchCoupons"
                :disabled="loading || loadingEndorserCodes || endorserCodes.length === 0"
                class="px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed min-w-[150px]"
              >
                <option value="">All</option>
                <option
                  v-for="code in endorserCodes"
                  :key="code"
                  :value="code"
                >
                  {{ code }}
                </option>
              </select>
            </template>
          </div>
          <div class="text-lg font-semibold text-gray-100">
            Total: {{ coupons.length }} coupon{{ coupons.length !== 1 ? 's' : '' }}
          </div>
        </div>
      </div>

      <!-- Generate Coupon Modal -->
      <div
        v-if="isGenerateModalOpen"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-warm-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">Generate Coupons</h2>
            <button
              @click="closeGenerateModal"
              class="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form @submit.prevent="handleGenerateCoupons" class="space-y-4">
            <!-- Number of Coupons -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Number of Coupons <span class="text-red-400">*</span>
              </label>
              <input
                v-model.number="formData.quantity"
                type="number"
                min="1"
                max="30"
                required
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter number (1-30)"
              />
              <p class="text-xs text-gray-500 mt-1">Min: 1, Max: 30</p>
            </div>

            <!-- Assigned To Endorser -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Assign To Endorser (Optional)
              </label>
              <select
                v-model="formData.assignedToEndorserId"
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">None</option>
                <option
                  v-for="endorser in endorsersList"
                  :key="endorser.id"
                  :value="endorser.id"
                >
                  {{ endorser.data.code }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">Leave empty if not assigning</p>
            </div>

            <!-- Commission Amount -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Commission Amount <span class="text-red-400">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2 text-gray-400">₱</span>
                <input
                  v-model.number="formData.commissionAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <!-- Discount Amount -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Discount Amount <span class="text-red-400">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2 text-gray-400">₱</span>
                <input
                  v-model.number="formData.discountAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="200.00"
                />
              </div>
              <p class="text-xs text-gray-500 mt-1">Default: 200</p>
            </div>

            <!-- Expiry Date (in days) -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Expiry Date (Days) <span class="text-red-400">*</span>
              </label>
              <input
                v-model.number="formData.expiryDays"
                type="number"
                min="1"
                max="60"
                required
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter days (1-60)"
              />
              <p class="text-xs text-gray-500 mt-1">Min: 1 day (24 hours), Max: 60 days</p>
            </div>

            <!-- Status Preview -->
            <div class="bg-warm-gray-900 rounded p-3">
              <p class="text-sm text-gray-400">Status will be:</p>
              <p class="text-lg font-semibold text-gray-100">
                {{ formData.assignedToEndorserId ? 'assigned' : 'active' }}
              </p>
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closeGenerateModal"
                class="flex-1 px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="isGenerating"
                class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isGenerating">Generating...</span>
                <span v-else>Generate</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Assign Coupon Modal -->
      <div
        v-if="isAssignModalOpen"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-warm-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">Assign Coupons</h2>
            <button
              @click="closeAssignModal"
              class="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form @submit.prevent="handleBulkAssign" class="space-y-4">
            <div class="bg-warm-gray-900 rounded p-3 mb-4">
              <p class="text-sm text-gray-400">Assigning <span class="font-semibold text-gray-100">{{ selectedCouponIds.size }}</span> coupon{{ selectedCouponIds.size !== 1 ? 's' : '' }}</p>
            </div>

            <!-- Assign To Endorser -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Assign To Endorser <span class="text-red-400">*</span>
              </label>
              <select
                v-model="assignFormData.assignedToEndorserId"
                required
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select an endorser</option>
                <option
                  v-for="endorser in endorsersList"
                  :key="endorser.id"
                  :value="endorser.id"
                >
                  {{ endorser.data.code }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">Select the endorser to assign these coupons to</p>
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closeAssignModal"
                class="flex-1 px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="isAssigning"
                class="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isAssigning">Assigning...</span>
                <span v-else>Assign</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <svg
          class="animate-spin h-8 w-8 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Coupons Table -->
      <div v-else-if="coupons.length > 0" class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
            <tr>
              <th class="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  @change="toggleSelectAll"
                  :disabled="loading || isPerformingAction || isGenerating"
                  class="w-4 h-4 text-blue-600 bg-warm-gray-900 border-warm-gray-700 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </th>
              <th class="px-4 py-3">Code</th>
              <th class="px-4 py-3">Status</th>
              <th class="px-4 py-3">Discount Amount</th>
              <th class="px-4 py-3">Commission Amount</th>
              <th class="px-4 py-3">Assigned To</th>
              <th class="px-4 py-3">Created By</th>
              <th class="px-4 py-3">Created At</th>
              <th class="px-4 py-3">Expiry Date</th>
              <th class="px-4 py-3">Used At</th>
              <th class="px-4 py-3">Used By</th>
            </tr>
          </thead>
          <tbody class="text-gray-300">
            <tr
              v-for="coupon in coupons"
              :key="coupon.id"
              :class="[
                'border-b border-warm-gray-800 transition-all',
                (isPerformingAction || isGenerating) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800/50'
              ]"
            >
              <td class="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  :checked="isSelected(coupon.id)"
                  @change="toggleSelect(coupon.id)"
                  :disabled="loading || isPerformingAction || isGenerating"
                  class="w-4 h-4 text-blue-600 bg-warm-gray-900 border-warm-gray-700 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </td>
              <td class="px-4 py-3">
                <div class="font-mono font-medium text-gray-100">{{ coupon.data.code }}</div>
              </td>
              <td class="px-4 py-3">
                <span
                  :class="[
                    'px-2 py-1 text-xs font-medium rounded',
                    getStatusColor(coupon.data.status)
                  ]"
                >
                  {{ coupon.data.status || 'N/A' }}
                </span>
              </td>
              <td class="px-4 py-3 text-gray-100">
                ₱{{ formatCurrency(coupon.data.discountAmount || 0) }}
              </td>
              <td class="px-4 py-3 text-gray-100">
                ₱{{ formatCurrency(coupon.data.commissionAmount || 0) }}
              </td>
              <td class="px-4 py-3">
                <div v-if="coupon.data.assignedToEndorserCode || coupon.data.assignedToEmail">
                  <div class="text-gray-100">{{ coupon.data.assignedToEndorserCode || 'N/A' }}</div>
                  <div class="text-xs text-gray-500">{{ coupon.data.assignedToEmail || '' }}</div>
                </div>
                <span v-else class="text-gray-500">Not assigned</span>
              </td>
              <td class="px-4 py-3">
                <div>
                  <div class="text-gray-100 text-xs">{{ coupon.data.createdByEmail || 'N/A' }}</div>
                </div>
              </td>
              <td class="px-4 py-3 text-gray-400">
                {{ formatFirestoreTimestamp(coupon.data.createdAt) }}
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <span :class="(isCouponExpired(coupon) && selectedStatus !== 'used' && selectedStatus !== '') ? 'text-red-400' : 'text-gray-400'">
                    {{ formatFirestoreTimestamp(coupon.data.expiryDate) }}
                  </span>
                  <span
                    v-if="isCouponExpired(coupon) && selectedStatus !== 'used' && selectedStatus !== ''"
                    class="px-2 py-0.5 text-xs font-medium rounded bg-red-600 text-white"
                  >
                    EXPIRED
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-gray-400">
                {{ formatFirestoreTimestamp(coupon.data.usedAt) }}
              </td>
              <td class="px-4 py-3">
                <div v-if="coupon.data.usedByEmail">
                  <div class="text-gray-100 text-xs">{{ coupon.data.usedByEmail }}</div>
                </div>
                <span v-else class="text-gray-500">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <p class="text-gray-400 text-lg">No coupons found</p>
        <p class="text-gray-500 text-sm mt-2">
          <span v-if="selectedStatus || selectedEndorserCode">
            No coupons found
            <span v-if="selectedStatus">with status "{{ selectedStatus }}"</span>
            <span v-if="selectedStatus && selectedEndorserCode"> and</span>
            <span v-if="selectedEndorserCode">for endorser "{{ selectedEndorserCode }}"</span>.
          </span>
          <span v-else>No coupons in the database.</span>
        </p>
      </div>

      <!-- Count Display -->
      <div v-if="!loading && coupons.length > 0" class="mt-6 text-sm text-gray-500">
        Showing {{ coupons.length }} coupon{{ coupons.length !== 1 ? 's' : '' }}
        <span v-if="selectedStatus || selectedEndorserCode">
          (<span v-if="selectedStatus">status: "{{ selectedStatus }}"</span><span v-if="selectedStatus && selectedEndorserCode">, </span><span v-if="selectedEndorserCode">endorser: "{{ selectedEndorserCode }}"</span>)
        </span>
      </div>

      <!-- Load More Button -->
      <div v-if="!loading && coupons.length > 0 && !isLastPage" class="mt-6 text-center">
        <button
          @click="loadMoreCoupons"
          :disabled="loadingMore"
          class="px-6 py-2 bg-warm-gray-800 hover:bg-warm-gray-700 text-gray-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-warm-gray-700"
        >
          <span v-if="loadingMore">Loading...</span>
          <span v-else>Load More</span>
        </button>
      </div>
    </div>

    <!-- Toast Notification -->
    <div
      v-if="toast.show"
      class="fixed bottom-4 right-4 z-50 transition-all duration-300"
      :class="toast.show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'"
    >
      <div
        :class="[
          'px-4 py-3 rounded-lg shadow-lg max-w-sm',
          toast.type === 'success' ? 'bg-green-900 text-green-100' : '',
          toast.type === 'error' ? 'bg-red-900 text-red-100' : '',
          toast.type === 'info' ? 'bg-blue-900 text-blue-100' : '',
          toast.type === 'loading' ? 'bg-gray-800 text-gray-100' : ''
        ]"
      >
        <div class="flex items-center gap-3">
          <div v-if="toast.type === 'loading'" class="flex-shrink-0">
            <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <div v-else-if="toast.type === 'success'" class="flex-shrink-0">
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          </div>
          <div v-else-if="toast.type === 'error'" class="flex-shrink-0">
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <p class="text-sm font-medium flex-1">{{ toast.message }}</p>
          <button
            @click="hideToast"
            class="flex-shrink-0 text-gray-400 hover:text-gray-200"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { db, auth } from '@/firebase/config.js'
import { collection, query, getDocs, where, orderBy, limit, startAfter, doc, getDoc, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore'
import { createCoupon, activateCoupon, deactivateCoupon, deleteCoupon, unassignCoupon, assignCoupon, updateCoupon } from '@/v3/CouponService'
import { listAllEndorserCodes } from '@/v3/EndorserService'

// Configuration
const PAGE_SIZE = 100

// Reactive state
const coupons = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const selectedStatus = ref('active') // Default to 'active'
const selectedEndorserCode = ref('') // Default to 'All'
const endorserCodes = ref([])
const loadingEndorserCodes = ref(false)

// Pagination state
const lastDocument = ref(null)
const isLastPage = ref(false)

// Month filter state (for used coupons)
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())

// Snapshot listener
let unsubscribeSnapshot = null

// Toast notification state
const toast = ref({
  show: false,
  message: '',
  type: 'info' // 'info', 'success', 'error', 'loading'
})

// Selected coupons state
const selectedCouponIds = ref(new Set())
const isPerformingAction = ref(false)

// Generate Modal State
const isGenerateModalOpen = ref(false)
const isGenerating = ref(false)
const endorsersList = ref([]) // Full endorser list with IDs

// Form Data
const formData = ref({
  quantity: 1,
  assignedToEndorserId: '',
  commissionAmount: 300,
  discountAmount: 200,
  expiryDays: 30
})

// Assign Modal State
const isAssignModalOpen = ref(false)
const isAssigning = ref(false)
const assignFormData = ref({
  assignedToEndorserId: ''
})


/**
 * Format Firestore Timestamp to readable date string
 */
const formatFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A'
  
  if (timestamp.seconds !== undefined) {
    const milliseconds = timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000)
    const date = new Date(milliseconds)
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    })
  }
  
  return 'N/A'
}

/**
 * Check if a coupon is expired
 */
const isCouponExpired = (coupon) => {
  if (!coupon.data.expiryDate) return false
  
  const currentDate = new Date()
  const expiryDate = coupon.data.expiryDate
  
  if (expiryDate.seconds !== undefined) {
    const expiryMilliseconds = expiryDate.seconds * 1000 + Math.floor(expiryDate.nanoseconds / 1000000)
    const expiryDateObj = new Date(expiryMilliseconds)
    return currentDate > expiryDateObj
  }
  
  return false
}

/**
 * Format currency amount
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Get status badge color
 */
const getStatusColor = (status) => {
  const colors = {
    active: 'bg-green-900 text-green-200',
    assigned: 'bg-purple-900 text-purple-200',
    used: 'bg-blue-900 text-blue-200',
    deactivated: 'bg-gray-700 text-gray-300',
    expired: 'bg-red-900 text-red-200'
  }
  return colors[status] || 'bg-gray-700 text-gray-300'
}

/**
 * Get month display string (e.g., "Nov 2024")
 */
const currentMonthDisplay = computed(() => {
  const date = new Date(currentYear.value, currentMonth.value, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
})

/**
 * Get start and end dates for the current month
 * @returns {{startDate: Date, endDate: Date}}
 */
const getMonthDateRange = () => {
  const startDate = new Date(currentYear.value, currentMonth.value, 1)
  startDate.setHours(0, 0, 0, 0)
  
  const endDate = new Date(currentYear.value, currentMonth.value + 1, 0)
  endDate.setHours(23, 59, 59, 999)
  
  return { startDate, endDate }
}

/**
 * Navigate to previous month
 */
const previousMonth = () => {
  // Prevent navigation if already loading
  if (loading.value) {
    return
  }
  
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
  fetchCoupons()
}

/**
 * Navigate to next month
 */
const nextMonth = () => {
  // Prevent navigation if already loading
  if (loading.value) {
    return
  }
  
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
  fetchCoupons()
}

/**
 * Fetch endorser codes from _endorser_codes collection
 */
const fetchEndorserCodes = async () => {
  loadingEndorserCodes.value = true
  try {
    const endorserCodesRef = collection(db, '_endorser_codes')
    const snapshot = await getDocs(endorserCodesRef)
    
    // Extract unique codes for filter dropdown
    const codes = new Set()
    // Store full endorser list with IDs for modal
    const endorsers = []
    
    snapshot.forEach(docSnapshot => {
      const data = docSnapshot.data()
      if (data.code) {
        codes.add(data.code)
        endorsers.push({
          id: docSnapshot.id,
          data: data
        })
      }
    })
    
    endorserCodes.value = Array.from(codes).sort()
    endorsersList.value = endorsers.sort((a, b) => a.data.code.localeCompare(b.data.code))
  } catch (error) {
    console.error('Error fetching endorser codes:', error)
    endorserCodes.value = []
    endorsersList.value = []
  } finally {
    loadingEndorserCodes.value = false
  }
}

/**
 * Build query for coupons
 */
const buildCouponsQuery = (lastDoc = null) => {
  const couponsRef = collection(db, 'coupons')
  const constraints = []
  
  // Add status filter if selected
  if (selectedStatus.value) {
    constraints.push(where('status', '==', selectedStatus.value))
  }
  
  // Add date filter for used coupons (filter by usedAt date)
  if (selectedStatus.value === 'used') {
    const { startDate, endDate } = getMonthDateRange()
    const startTimestamp = Timestamp.fromDate(startDate)
    const endTimestamp = Timestamp.fromDate(endDate)
    constraints.push(where('usedAt', '>=', startTimestamp))
    constraints.push(where('usedAt', '<=', endTimestamp))
  }
  
  // Add endorser code filter if selected
  if (selectedEndorserCode.value) {
    constraints.push(where('assignedToEndorserCode', '==', selectedEndorserCode.value))
  }
  
  // Order by creation date (newest first) or usedAt if filtering by used status
  if (selectedStatus.value === 'used') {
    constraints.push(orderBy('usedAt', 'desc'))
  } else {
    constraints.push(orderBy('createdAt', 'desc'))
  }
  
  // Add pagination if loading more
  if (lastDoc) {
    constraints.push(startAfter(lastDoc))
  }
  
  // Add limit
  constraints.push(limit(PAGE_SIZE))
  
  return query(couponsRef, ...constraints)
}

/**
 * Set up real-time snapshot listener for coupons
 */
const setupSnapshotListener = () => {
  // Clean up existing listener if any
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot()
    unsubscribeSnapshot = null
  }
  
  // Reset pagination
  lastDocument.value = null
  isLastPage.value = false
  
  loading.value = true
  
  try {
    const q = buildCouponsQuery(null)
    
    unsubscribeSnapshot = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          // Map documents to array
          coupons.value = querySnapshot.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }))
          
          // Update pagination info
          if (querySnapshot.docs.length > 0) {
            lastDocument.value = querySnapshot.docs[querySnapshot.docs.length - 1]
            isLastPage.value = querySnapshot.size < PAGE_SIZE
          } else {
            lastDocument.value = null
            isLastPage.value = true
          }
        } catch (error) {
          console.error('Error processing snapshot:', error)
        } finally {
          loading.value = false
        }
      },
      (error) => {
        console.error('Error in snapshot listener:', error)
        loading.value = false
        // If error is due to missing index, show helpful message
        if (error.message && error.message.includes('index')) {
          console.warn('Firestore index may be required. Check the error message for index creation link.')
          showToast('Error: Firestore index may be required. Check console for details.', 'error')
        }
      }
    )
  } catch (error) {
    console.error('Error setting up snapshot listener:', error)
    loading.value = false
  }
}

/**
 * Handle status change - reset endorser filter and fetch coupons
 */
const handleStatusChange = () => {
  // Reset endorser filter when status changes
  selectedEndorserCode.value = ''
  // Reset month to current month when switching to used status
  if (selectedStatus.value === 'used') {
    currentMonth.value = new Date().getMonth()
    currentYear.value = new Date().getFullYear()
  }
  fetchCoupons()
}

/**
 * Fetch coupons from Firestore based on selected status and endorser code
 */
const fetchCoupons = async () => {
  // Reset selected coupons when filters change
  selectedCouponIds.value.clear()
  setupSnapshotListener()
}

/**
 * Load more coupons (pagination)
 */
const loadMoreCoupons = async () => {
  if (!lastDocument.value || isLastPage.value || loadingMore.value) {
    return
  }

  loadingMore.value = true
  try {
    const q = buildCouponsQuery(lastDocument.value)
    const snapshot = await getDocs(q)
    
    // Map documents to array
    const newCoupons = snapshot.docs.map(doc => ({
      id: doc.id,
      data: doc.data()
    }))
    
    // Append to existing coupons
    coupons.value = [...coupons.value, ...newCoupons]
    
    // Update pagination info
    if (snapshot.docs.length > 0) {
      lastDocument.value = snapshot.docs[snapshot.docs.length - 1]
      isLastPage.value = snapshot.size < PAGE_SIZE
    } else {
      isLastPage.value = true
    }
  } catch (error) {
    console.error('Error loading more coupons:', error)
    showToast('Error loading more coupons', 'error')
  } finally {
    loadingMore.value = false
  }
}

/**
 * Open generate coupon modal
 */
const openGenerateModal = () => {
  isGenerateModalOpen.value = true
  // Reset form to defaults
  formData.value = {
    quantity: 1,
    assignedToEndorserId: '',
    commissionAmount: 300,
    discountAmount: 200,
    expiryDays: 30
  }
}

/**
 * Close generate coupon modal
 */
const closeGenerateModal = () => {
  isGenerateModalOpen.value = false
}

/**
 * Open assign coupon modal
 */
const openAssignModal = () => {
  if (selectedCouponIds.value.size === 0) return
  assignFormData.value = {
    assignedToEndorserId: ''
  }
  isAssignModalOpen.value = true
}

/**
 * Close assign coupon modal
 */
const closeAssignModal = () => {
  isAssignModalOpen.value = false
  assignFormData.value = {
    assignedToEndorserId: ''
  }
}


/**
 * Generate expiry date from days
 */
const generateExpiryDate = (days) => {
  const currentDate = new Date()
  currentDate.setDate(currentDate.getDate() + days)
  return Timestamp.fromDate(currentDate)
}

/**
 * Get user email from user ID
 */
const getUserEmail = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId))
    if (userDoc.exists()) {
      return userDoc.data().email || ''
    }
  } catch (error) {
    console.error('Error fetching user email:', error)
  }
  return ''
}

/**
 * Show toast notification
 */
const showToast = (message, type = 'info', duration = 5000) => {
  toast.value = {
    show: true,
    message,
    type
  }
  
  // Auto hide after duration (except for loading)
  if (type !== 'loading' && duration > 0) {
    setTimeout(() => {
      hideToast()
    }, duration)
  }
}

/**
 * Hide toast notification
 */
const hideToast = () => {
  toast.value.show = false
}

/**
 * Check if a coupon is selected
 */
const isSelected = (couponId) => {
  return selectedCouponIds.value.has(couponId)
}

/**
 * Toggle selection for a single coupon
 */
const toggleSelect = (couponId) => {
  if (selectedCouponIds.value.has(couponId)) {
    selectedCouponIds.value.delete(couponId)
  } else {
    selectedCouponIds.value.add(couponId)
  }
}

/**
 * Check if all visible coupons are selected
 */
const isAllSelected = computed(() => {
  if (coupons.value.length === 0) return false
  return coupons.value.every(coupon => selectedCouponIds.value.has(coupon.id))
})

/**
 * Toggle select all visible coupons
 */
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    // Deselect all visible coupons
    coupons.value.forEach(coupon => {
      selectedCouponIds.value.delete(coupon.id)
    })
  } else {
    // Select all visible coupons
    coupons.value.forEach(coupon => {
      selectedCouponIds.value.add(coupon.id)
    })
  }
}

/**
 * Get selected coupon objects
 */
const getSelectedCoupons = () => {
  return coupons.value.filter(coupon => selectedCouponIds.value.has(coupon.id))
}

/**
 * Handle bulk deactivate action
 */
const handleBulkDeactivate = async () => {
  if (selectedCouponIds.value.size === 0) return

  isPerformingAction.value = true
  showToast('Deactivating coupons...', 'loading', 0)

  const selectedCoupons = getSelectedCoupons()
  const results = { success: 0, errors: [] }

  try {
    for (const coupon of selectedCoupons) {
      try {
        // Check if coupon is expired - if so, use updateCoupon directly to bypass expired check
        if (isCouponExpired(coupon)) {
          // For expired coupons, directly update status to deactivated
          await updateCoupon(coupon.id, { status: 'deactivated' }, 'deactivated')
        } else {
          // For non-expired coupons, use the regular deactivateCoupon function
          await deactivateCoupon(coupon)
        }
        results.success++
      } catch (error) {
        console.error(`Error deactivating coupon ${coupon.data.code}:`, error)
        results.errors.push(`${coupon.data.code}: ${error.message || 'Failed to deactivate'}`)
      }
    }

    // Clear selection
    selectedCouponIds.value.clear()

    // Show result toast
    if (results.errors.length > 0) {
      showToast(`Deactivated ${results.success} coupon(s). ${results.errors.length} error(s).`, 'error')
    } else {
      showToast(`Successfully deactivated ${results.success} coupon(s)!`, 'success')
    }
  } catch (error) {
    console.error('Error in bulk deactivate:', error)
    showToast('Error deactivating coupons', 'error')
  } finally {
    isPerformingAction.value = false
  }
}

/**
 * Handle bulk activate action
 */
const handleBulkActivate = async () => {
  if (selectedCouponIds.value.size === 0) return

  isPerformingAction.value = true
  showToast('Activating coupons...', 'loading', 0)

  const selectedCoupons = getSelectedCoupons()
  const results = { success: 0, errors: [] }

  try {
    for (const coupon of selectedCoupons) {
      try {
        // Note: activateCoupon automatically sets status to 'assigned' if assignedToId exists, otherwise 'active'
        await activateCoupon(coupon)
        results.success++
      } catch (error) {
        console.error(`Error activating coupon ${coupon.data.code}:`, error)
        results.errors.push(`${coupon.data.code}: ${error.message || 'Failed to activate'}`)
      }
    }

    // Clear selection
    selectedCouponIds.value.clear()

    // Show result toast
    if (results.errors.length > 0) {
      showToast(`Activated ${results.success} coupon(s). ${results.errors.length} error(s).`, 'error')
    } else {
      showToast(`Successfully activated ${results.success} coupon(s)!`, 'success')
    }
  } catch (error) {
    console.error('Error in bulk activate:', error)
    showToast('Error activating coupons', 'error')
  } finally {
    isPerformingAction.value = false
  }
}

/**
 * Handle bulk unassign action
 */
const handleBulkUnassign = async () => {
  if (selectedCouponIds.value.size === 0) return

  isPerformingAction.value = true
  showToast('Unassigning coupons...', 'loading', 0)

  const selectedCoupons = getSelectedCoupons()
  const results = { success: 0, errors: [] }

  try {
    for (const coupon of selectedCoupons) {
      try {
        await unassignCoupon(coupon)
        results.success++
      } catch (error) {
        console.error(`Error unassigning coupon ${coupon.data.code}:`, error)
        results.errors.push(`${coupon.data.code}: ${error.message || 'Failed to unassign'}`)
      }
    }

    // Clear selection
    selectedCouponIds.value.clear()

    // Show result toast
    if (results.errors.length > 0) {
      showToast(`Unassigned ${results.success} coupon(s). ${results.errors.length} error(s).`, 'error')
    } else {
      showToast(`Successfully unassigned ${results.success} coupon(s)!`, 'success')
    }
  } catch (error) {
    console.error('Error in bulk unassign:', error)
    showToast('Error unassigning coupons', 'error')
  } finally {
    isPerformingAction.value = false
  }
}

/**
 * Handle bulk assign action
 */
const handleBulkAssign = async () => {
  if (selectedCouponIds.value.size === 0) return

  // Validate form BEFORE closing modal
  const selectedEndorserId = assignFormData.value.assignedToEndorserId
  console.log('Assign form data:', assignFormData.value)
  console.log('Selected endorser ID:', selectedEndorserId)
  
  if (!selectedEndorserId || selectedEndorserId === '' || selectedEndorserId === null || selectedEndorserId === undefined) {
    showToast('Please select an endorser', 'error')
    return
  }

  // Close modal after validation passes
  closeAssignModal()

  // Show loading toast
  showToast('Assigning coupons...', 'loading', 0)
  
  isAssigning.value = true
  isPerformingAction.value = true

  const selectedCoupons = getSelectedCoupons()
  const results = { success: 0, errors: [] }

  try {
    // Find the selected endorser (use the variable we validated)
    const selectedEndorser = endorsersList.value.find(
      e => e.id === selectedEndorserId
    )
    
    if (!selectedEndorser) {
      throw new Error('Selected endorser not found')
    }

    // Get user email for the endorser
    const assignedToEmail = await getUserEmail(selectedEndorser.id)
    
    // Create user object for assignCoupon function
    const user = {
      id: selectedEndorser.id,
      email: assignedToEmail || '',
      endorserCode: selectedEndorser.data.code
    }

    // Assign each coupon
    for (const coupon of selectedCoupons) {
      try {
        await assignCoupon(coupon, user)
        results.success++
      } catch (error) {
        console.error(`Error assigning coupon ${coupon.data.code}:`, error)
        results.errors.push(`${coupon.data.code}: ${error.message || 'Failed to assign'}`)
      }
    }

    // Clear selection
    selectedCouponIds.value.clear()

    // Show result toast
    if (results.errors.length > 0) {
      showToast(`Assigned ${results.success} coupon(s). ${results.errors.length} error(s).`, 'error')
    } else {
      showToast(`Successfully assigned ${results.success} coupon(s)!`, 'success')
    }
  } catch (error) {
    console.error('Error in bulk assign:', error)
    showToast('Error assigning coupons', 'error')
  } finally {
    isAssigning.value = false
    isPerformingAction.value = false
  }
}

/**
 * Handle bulk delete action
 */
const handleBulkDelete = async () => {
  if (selectedCouponIds.value.size === 0) return

  // Confirm deletion
  const confirmed = confirm(`Are you sure you want to delete ${selectedCouponIds.value.size} coupon(s)? This action cannot be undone.`)
  if (!confirmed) return

  isPerformingAction.value = true
  showToast('Deleting coupons...', 'loading', 0)

  const selectedCoupons = getSelectedCoupons()
  const results = { success: 0, errors: [] }

  try {
    for (const coupon of selectedCoupons) {
      try {
        await deleteCoupon(coupon)
        results.success++
        // Remove from selected set immediately
        selectedCouponIds.value.delete(coupon.id)
      } catch (error) {
        console.error(`Error deleting coupon ${coupon.data.code}:`, error)
        results.errors.push(`${coupon.data.code}: ${error.message || 'Failed to delete'}`)
      }
    }

    // Clear selection
    selectedCouponIds.value.clear()

    // Show result toast
    if (results.errors.length > 0) {
      showToast(`Deleted ${results.success} coupon(s). ${results.errors.length} error(s).`, 'error')
    } else {
      showToast(`Successfully deleted ${results.success} coupon(s)!`, 'success')
    }
  } catch (error) {
    console.error('Error in bulk delete:', error)
    showToast('Error deleting coupons', 'error')
  } finally {
    isPerformingAction.value = false
  }
}

/**
 * Handle generate coupons form submission
 */
const handleGenerateCoupons = async () => {
  // Close modal immediately
  closeGenerateModal()
  
  // Show loading toast
  showToast('Generating coupons...', 'loading', 0)
  
  isGenerating.value = true

  try {
    // Validate form
    if (formData.value.quantity < 1 || formData.value.quantity > 30) {
      throw new Error('Number of coupons must be between 1 and 30')
    }

    if (formData.value.expiryDays < 1 || formData.value.expiryDays > 60) {
      throw new Error('Expiry days must be between 1 and 60')
    }

    // Get endorser info if assigned
    let assignedToId = null
    let assignedToEmail = null
    let assignedToEndorserCode = null
    let status = 'active'

    if (formData.value.assignedToEndorserId) {
      const selectedEndorser = endorsersList.value.find(
        e => e.id === formData.value.assignedToEndorserId
      )
      
      if (selectedEndorser) {
        assignedToId = selectedEndorser.id
        assignedToEndorserCode = selectedEndorser.data.code
        assignedToEmail = await getUserEmail(selectedEndorser.id)
        status = 'assigned'
      }
    }

    // Generate expiry date
    const expiryDate = generateExpiryDate(formData.value.expiryDays)

    // Generate coupons
    const generatedCoupons = []
    const errors = []

    for (let i = 0; i < formData.value.quantity; i++) {
      try {
        const couponData = {
          discountAmount: formData.value.discountAmount,
          expiryDate: expiryDate,
          assignedToId: assignedToId,
          assignedToEmail: assignedToEmail
        }

        // Create coupon first
        const coupon = await createCoupon(couponData)
        
        // Update coupon with additional fields that createCoupon doesn't handle
        const updates = {}
        
        // Add commission amount
        if (formData.value.commissionAmount) {
          updates.commissionAmount = formData.value.commissionAmount
        }
        
        // Add endorser code if assigned
        if (assignedToEndorserCode) {
          updates.assignedToEndorserCode = assignedToEndorserCode
        }
        
        // Update status if assigned
        if (status === 'assigned') {
          updates.status = 'assigned'
          updates.assignedAt = new Date()
        }
        
        // Apply updates if any
        if (Object.keys(updates).length > 0) {
          await updateDoc(doc(db, 'coupons', coupon.id), updates)
          
          // Update the coupon object with the new values
          Object.assign(coupon.data, updates)
        }
        
        generatedCoupons.push(coupon)
      } catch (error) {
        console.error(`Error generating coupon ${i + 1}:`, error)
        errors.push(`Coupon ${i + 1}: ${error.message || 'Unknown error'}`)
      }
    }

    // Update toast with result (replaces loading toast)
    if (errors.length > 0) {
      const errorMsg = `Generated ${generatedCoupons.length} coupons. Errors: ${errors.join(', ')}`
      showToast(errorMsg, 'error')
    } else {
      const successMsg = `Successfully generated ${generatedCoupons.length} coupon${generatedCoupons.length !== 1 ? 's' : ''}!`
      showToast(successMsg, 'success')
      
      // Real-time listener will automatically update the coupon list
      // No need to manually refresh as snapshot listener handles it
    }
  } catch (error) {
    console.error('Error generating coupons:', error)
    const errorMsg = error.message || 'Failed to generate coupons'
    showToast(errorMsg, 'error')
  } finally {
    isGenerating.value = false
  }
}

// Fetch endorser codes and coupons on mount
onMounted(async () => {
  await fetchEndorserCodes()
  await fetchCoupons() // This now uses snapshot listener for real-time updates
})

// Clean up snapshot listener on unmount
onBeforeUnmount(() => {
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot()
    unsubscribeSnapshot = null
  }
})
</script>

<style scoped>
/* Custom select arrow styling */
select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  appearance: none;
}
</style>